import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import useAuthStore from '@/store/authStore';
import useNotificationStore from '@/store/notificationStore';
import { resolveAvatarUrl } from '@/utils/avatar';
import ThemeToggle from '@/components/ui/ThemeToggle';
import { Coin3D, Gem3D, Energy3D } from '@/components/ui/GameIcons';

const XP_THRESHOLDS = [0, 500, 1200, 2500, 4500, 7500, 12000, 18000, 26000, 36000];
const LEVEL_LABELS = ['I','II','III','IV','V','VI','VII','VIII','IX','X'];

function getXpProgress(xp, level) {
  const lvl = Math.max(1, Math.min(level || 1, 10)) - 1;
  const current = XP_THRESHOLDS[lvl] || 0;
  const next = XP_THRESHOLDS[lvl + 1] || current + 500;
  const pct = Math.min(100, Math.round(((xp - current) / (next - current)) * 100));
  return { current: xp - current, needed: next - current, pct: isNaN(pct) ? 0 : pct };
}

function StatPill({ icon: IconComp, value, label, tone, animateKey, id }) {
  return (
    <motion.div
      key={animateKey}
      animate={animateKey !== undefined ? { scale: [1, 1.18, 1] } : {}}
      transition={{ duration: 0.35 }}
    >
      <div className="hud-pill" id={id}>
        <IconComp className="h-5 w-5 flex-shrink-0 drop-shadow-sm" />
        <span className={`text-[11px] font-black leading-none tabular-nums ${tone}`}>{value}</span>
        <span className="sr-only">{label}</span>
      </div>
    </motion.div>
  );
}

export default function TopHUD() {
  const { player } = useAuthStore();
  const navigate = useNavigate();
  const offerCount = useNotificationStore((s) => s.offerCount);

  if (!player) return null;

  const xp = player.xp || 0;
  const level = player.level || 1;
  const { current, needed, pct } = getXpProgress(xp, level);
  const avatarUrl = resolveAvatarUrl(player.avatarUrl);
  const cityName = player.city?.name || 'Aveiro, Portugal';
  const levelLabel = LEVEL_LABELS[(level - 1)] || `${level}`;

  return (
    <header
      className="fixed left-0 right-0 top-0 z-30 border-b border-edge bg-surface/95 backdrop-blur-md"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        boxShadow: '0 4px 20px rgb(var(--hc-bg) / 0.5)',
      }}
    >
      {/* linha principal */}
      <div className="flex items-center gap-2 px-3 py-2">
        {/* avatar + nível */}
        <button
          type="button"
          onClick={() => navigate('/game/profile')}
          className="relative flex-shrink-0 focus:outline-none"
          aria-label={`Perfil, nível ${level}`}
        >
          <div
            className="h-11 w-11 overflow-hidden rounded-full"
            style={{
              border: '2px solid rgb(var(--hc-accent))',
              boxShadow: '0 0 0 1px rgb(var(--hc-accent) / 0.3), 0 0 12px rgb(var(--hc-accent) / 0.25)',
            }}
          >
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          </div>
          <div
            className="absolute -bottom-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 font-urban text-[9px] font-black text-white"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--hc-accent-neon)) 0%, rgb(var(--hc-accent)) 100%)',
              border: '1.5px solid rgb(var(--hc-surface))',
              boxShadow: '0 1px 4px rgb(var(--hc-bg) / 0.5)',
            }}
          >
            {levelLabel}
          </div>
        </button>

        {/* stats */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <StatPill
            id="hud-coins"
            icon={Coin3D}
            value={(player.coins || 0).toLocaleString('pt-PT')}
            label="Moedas"
            tone="text-fg"
            animateKey={player.coins}
          />
          <StatPill icon={Gem3D} value={player.gems || 0} label="Gemas" tone="text-fg" />
          <StatPill
            icon={Energy3D}
            value={`${player.energy ?? 30}/${player.energyMax ?? 30}`}
            label="Energia"
            tone="text-fg"
          />
        </div>

        {/* alternância de tema */}
        <ThemeToggle className="flex-shrink-0" />

        {/* botão loja */}
        <button
          type="button"
          onClick={() => navigate('/game/shop')}
          className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-accent focus:outline-none animate-glow-pulse"
          style={{
            background: 'linear-gradient(135deg, rgb(var(--hc-accent) / 0.35) 0%, rgb(var(--hc-accent) / 0.15) 100%)',
            border: '1.5px solid rgb(var(--hc-accent) / 0.8)',
            boxShadow: '0 0 12px rgb(var(--hc-accent) / 0.45), 0 2px 8px rgb(var(--hc-bg) / 0.4)',
          }}
          aria-label="Ofertas"
        >
          <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
            <path d="M6 2 3 7v1a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0V7L18 2H6Z" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <path d="M3 8v11a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M10 21v-6h4v6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
          {offerCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-black text-white shadow-[0_0_6px_rgba(220,38,38,0.6)]">
              {offerCount}
            </span>
          )}
        </button>
      </div>

      {/* barra XP */}
      <div className="flex items-center gap-2 px-3 pb-2">
        <div className="flex items-center gap-1 text-accent">
          <svg viewBox="0 0 12 12" fill="none" className="h-2.5 w-2.5 flex-shrink-0" aria-hidden>
            <polygon points="6,1 7.8,4.5 11.5,5 8.8,7.7 9.5,11.5 6,9.5 2.5,11.5 3.2,7.7 0.5,5 4.2,4.5" fill="currentColor" opacity="0.9"/>
          </svg>
          <span className="text-[9px] font-black uppercase tracking-wider">XP</span>
        </div>
        <div className="relative flex-1 overflow-hidden rounded-full border border-edge bg-surfaceAlt" style={{ height: '5px' }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg, rgb(var(--hc-accent)) 0%, rgb(var(--hc-accent-neon)) 100%)',
              boxShadow: '0 0 6px rgb(var(--hc-accent) / 0.5)',
            }}
          />
        </div>
        <span className="text-[9px] font-black text-muted tabular-nums">
          {current.toLocaleString('pt-PT')}<span className="opacity-50"> / {needed.toLocaleString('pt-PT')}</span>
        </span>
        <div className="flex items-center gap-1 text-[9px] font-bold text-muted">
          <svg viewBox="0 0 12 12" fill="none" className="h-2.5 w-2.5 flex-shrink-0 text-accent" aria-hidden>
            <path d="M6 1C3.8 1 2 2.8 2 5c0 3 4 7 4 7s4-4 4-7c0-2.2-1.8-4-4-4Z" fill="currentColor" fillOpacity="0.25" stroke="currentColor" strokeWidth="1"/>
            <circle cx="6" cy="5" r="1.2" fill="currentColor"/>
          </svg>
          <span className="truncate max-w-[70px]">{cityName}</span>
        </div>
      </div>
    </header>
  );
}
